'use client';

import { Dialog, Flex, Text, ButtonGroup, Button } from '@/_components';
import { useContext, useCallback, type FC } from 'react';
import { downloadMultipleDocuments } from '@/_api/client-documents';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { DownloadMultipleDocumentsDialogProps } from './DownloadMultipleDocumentsDialog.types';

export const DownloadMultipleDocumentsDialog: FC<
  DownloadMultipleDocumentsDialogProps
> = ({ isOpen, onClose, documents, companyId, onDownloaded }) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const handleDownloadMultiple = useCallback(
    async (convertImageToPdf: boolean) => {
      try {
        await downloadMultipleDocuments({
          companyId,
          documents,
          convertImageToPdf,
          token,
        });

        onDownloaded();
        addToast(
          'success',
          'You will receive a notification when the zip file is ready for download.',
        );
      } catch (e) {
        console.error(e);
        addToast('error', 'There was a problem downloading the folder.');
      } finally {
        onClose();
      }
    },
    [companyId, documents, addToast, onClose, token, onDownloaded],
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Convert and download">
      <Dialog.Content>
        <Flex isColumn gap={6}>
          <Text>
            Do you want to convert all images to PDFs and download? This process
            might take a few minutes. You will receive a notification when the
            zip file is ready for download.
          </Text>
        </Flex>
      </Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button
            intent="secondary"
            onClick={() => handleDownloadMultiple(false)}
            value="No. Don't convert images to PDFs."
          />
          <Button
            intent="primary"
            onClick={() => handleDownloadMultiple(true)}
            value="Yes. Convert images to PDFs."
          />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
